import { getCurrentThread } from '@/store/reducers/UIState';
import {
  mkRelatedRowDeleteBtnTrackAttrs,
  mkRelatedRowMoreMenuBtnTrackAttrs,
  mkRowDeleteBtnTrackAttrs,
  mkRowMoreMenuBtnTrackAttrs,
} from '@/analytics/props/litTable';
import { PopoverListButton, TableViewMode } from '@/components/table/TableView';
import { removeTableRows } from '@/store/thunks/UpdateTable';
import { TableRow } from '@/models/Table';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { Fragment, ReactNode, useCallback, useRef, useState } from 'react';
import { IconButton, List, Popover, PopoverOrigin } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const popoverAnchorPosition: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};

type RowSettingsDropdownProps = {
  row: TableRow;
  mode?: TableViewMode;
};

export const RowSettingsDropdown = ({
  row,
  mode = TableViewMode.LIT_REVIEW,
}: RowSettingsDropdownProps): ReactNode => {
  const dispatch = useAppDispatch();
  const currentThread = useAppSelector(getCurrentThread);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [openStates, setOpenStates] = useState<{
    isPopoverOpen: boolean;
  }>({ isPopoverOpen: false });

  const handleOpenPopover = useCallback(() => {
    setOpenStates((state) => ({ ...state, isPopoverOpen: true }));
  }, [setOpenStates]);

  const handleClosePopover = useCallback(() => {
    setOpenStates((state) => ({ ...state, isPopoverOpen: false }));
  }, [setOpenStates]);

  const handleDeleteRow = useCallback(() => {
    if (!currentThread) {
      return;
    }

    dispatch(
      removeTableRows({
        currentThreadId: currentThread.id,
        rowIds: [row.id],
      }),
    );
  }, [row, currentThread]);

  return (
    <Fragment>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          padding: (theme) => theme.spacing(0.5),
        }}
        ref={buttonRef}
        {...(mode === TableViewMode.LIT_REVIEW
          ? mkRowMoreMenuBtnTrackAttrs({ rowId: row.id })
          : mkRelatedRowMoreMenuBtnTrackAttrs({ rowId: row.id }))}
      >
        <MoreHorizIcon
          fontSize="small"
          sx={{
            color: 'rgba(250, 242, 243, 0.8)', // cream-80
          }}
        />
      </IconButton>

      <Popover
        open={openStates.isPopoverOpen}
        anchorEl={buttonRef.current}
        onClose={handleClosePopover}
        anchorOrigin={popoverAnchorPosition}
      >
        <List>
          <PopoverListButton
            onClick={handleDeleteRow}
            {...(mode === TableViewMode.LIT_REVIEW
              ? mkRowDeleteBtnTrackAttrs({ rowId: row.id })
              : mkRelatedRowDeleteBtnTrackAttrs({ rowId: row.id }))}
          >
            <DeleteOutlineIcon />
            Delete Row
          </PopoverListButton>
        </List>
      </Popover>
    </Fragment>
  );
};
